(() => {
  if (window.hvSearchInitialized) return;
  window.hvSearchInitialized = true;
  const instances = new Map();
  function init(search) {
  if (instances.has(search)) return;
  const events = new AbortController();
  const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: events.signal });

  if (!search) return;

  const openButton = search.querySelector("[data-search-open]");
  const overlay = search.querySelector("[data-search-overlay]");
  if (!overlay || !openButton) return;
  if (overlay) {
    document.body.appendChild(overlay);
  }
  const panel = overlay.querySelector("[data-search-panel]");
  const closeButton = overlay.querySelector("[data-search-close]");
  const input = overlay.querySelector("[data-search-input]");
  const results = overlay.querySelector("[data-search-results]");
  const viewAll = overlay.querySelector("[data-search-all]");

  const searchUrl = search.dataset.searchUrl;
  const resultsUrl = search.dataset.resultsUrl;

  let debounceTimer;
  let controller;
  let previousFocus = null;
  let previousBodyOverflow = "";

  const groups = [
    { key: "products", label: "Products", singular: "Product" },
    { key: "collections", label: "Collections", singular: "Collection" },
    { key: "pages", label: "Pages", singular: "Page" },
    { key: "articles", label: "Articles", singular: "Article" },
  ];

  function setMessage(message) {
    results.replaceChildren();

    const paragraph = document.createElement("p");
    paragraph.className = "hv-search__message";
    paragraph.textContent = message;

    results.appendChild(paragraph);
  }

  function openSearch() {
    if (!overlay.hidden) return;
    previousFocus = document.activeElement;
    previousBodyOverflow = document.body.style.overflow;

    overlay.hidden = false;
    openButton.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";

    if (input.value.trim().length >= 2) fetchResults(input.value.trim());
    requestAnimationFrame(() => {
      if (!overlay.hidden) input.focus();
    });
  }

  function closeSearch() {
    if (overlay.hidden) return;
    clearTimeout(debounceTimer);
    controller?.abort();

    overlay.hidden = true;
    openButton.setAttribute("aria-expanded", "false");
    document.body.style.overflow = previousBodyOverflow;

    if (previousFocus && previousFocus.isConnected) {
      previousFocus.focus();
    } else {
      openButton.focus();
    }
  }

  function createResultItem(item, type) {
    const link = document.createElement("a");
    link.className = "hv-search__item";
    link.href = item.url || "#";

    const imageBox = document.createElement("span");
    imageBox.className = "hv-search__item-image";

    const imageSource =
      item.featured_image?.url ||
      item.featured_image?.src ||
      item.image?.url ||
      item.image?.src ||
      (typeof item.image === "string" ? item.image : null);

    if (imageSource) {
      const image = document.createElement("img");
      image.src = imageSource;
      image.alt = item.title || "";
      image.loading = "lazy";
      imageBox.appendChild(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "hv-search__item-placeholder";
      placeholder.textContent = "✦";
      imageBox.appendChild(placeholder);
    }

    const content = document.createElement("span");
    content.className = "hv-search__item-content";

    const title = document.createElement("span");
    title.className = "hv-search__item-title";
    title.textContent = item.title || "Untitled";

    const meta = document.createElement("span");
    meta.className = "hv-search__item-meta";
    meta.textContent = type;

    content.appendChild(title);
    content.appendChild(meta);

    const arrow = document.createElement("span");
    arrow.className = "hv-search__item-arrow";
    arrow.textContent = "↗";
    arrow.setAttribute("aria-hidden", "true");

    link.appendChild(imageBox);
    link.appendChild(content);
    link.appendChild(arrow);

    return link;
  }

  function renderResults(data, query) {
    results.replaceChildren();

    const availableResults = data?.resources?.results || {};
    let total = 0;

    groups.forEach((group) => {
      const items = availableResults[group.key] || [];

      if (!items.length) return;

      const heading = document.createElement("h3");
      heading.className = "hv-search__group-title";
      heading.textContent = group.label;

      results.appendChild(heading);

      items.forEach((item) => {
        results.appendChild(createResultItem(item, group.singular));
        total++;
      });
    });

    if (total === 0) {
      setMessage(`No results found for "${query}".`);
    }

    const allResultsUrl = new URL(resultsUrl, window.location.origin);
    allResultsUrl.searchParams.set("q", query);
    allResultsUrl.searchParams.set("type", "product,page,article");

    viewAll.href = allResultsUrl.toString();
    viewAll.hidden = false;
  }

  async function fetchResults(query) {
    if (search.dataset.predictive === "false") { setMessage("Press Search to see results."); return; }
    controller?.abort();
    controller = new AbortController();
    const currentController = controller;

    setMessage("Searching...");
    viewAll.hidden = true;

    const url = new URL(searchUrl, window.location.origin);

    url.searchParams.set("q", query);
    url.searchParams.set("resources[type]", "product,collection,page,article");
    url.searchParams.set("resources[limit]", search.dataset.limit || "5");
    url.searchParams.set("resources[limit_scope]", "each");

    try {
      const response = await fetch(url.toString(), {
        signal: currentController.signal,
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();

      if (currentController.signal.aborted || input.value.trim() !== query) {
        return;
      }

      renderResults(data, query);
    } catch (error) {
      if (error.name === "AbortError" || currentController.signal.aborted || input.value.trim() !== query) return;

      console.error("Happy Vibes search:", error);
      setMessage("Search is temporarily unavailable. Try again.");

      const fallbackUrl = new URL(resultsUrl, window.location.origin);
      fallbackUrl.searchParams.set("q", query);

      viewAll.href = fallbackUrl.toString();
      viewAll.hidden = false;
    }
  }

  listen(openButton, "click", openSearch);
  listen(closeButton, "click", closeSearch);

  listen(overlay, "click", (event) => {
    if (event.target === overlay) {
      closeSearch();
    }
  });

  listen(document, "keydown", (event) => {
    if (overlay.hidden) return;

    if (event.key === "Escape") {
      closeSearch();
      return;
    }

    if (event.key === "Tab") {
      const focusable = Array.from(
        panel.querySelectorAll(
          "a[href]:not([hidden]), button:not([disabled]), input:not([disabled])",
        ),
      ).filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  listen(input, "input", () => {
    clearTimeout(debounceTimer);
    controller?.abort();

    const query = input.value.trim();

    if (query.length < 2) {
      viewAll.hidden = true;
      setMessage("Type at least 2 characters to search.");
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchResults(query);
    }, 300);
  });
  instances.set(search, () => {
    if (!overlay.hidden) closeSearch();
    clearTimeout(debounceTimer);
    controller?.abort();
    events.abort();
    overlay.remove();
    instances.delete(search);
  });
  }
  const scan = () => document.querySelectorAll("[data-hv-search]").forEach(init);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
  else scan();
  document.addEventListener("shopify:section:load", scan);
  document.addEventListener("shopify:section:unload", event => {
    for (const [search, cleanup] of instances) if (event.target.contains(search)) cleanup();
  });
})();
